/* EMM hero — low-poly Mac mini with lo-fi posterized shading + data stream.
   Geometry decimated from "Apple Mac mini M4 pro" by mistjs
   (https://skfb.ly/pstXu), used with the author's permission for commercial
   use with attribution. Mesh ships in emm-lowpoly-mesh.js (Int16/Uint16).
   Falls back to the point-cloud asset (emm-cloud-points.js) if present instead.
   Raw WebGL, no libraries. Slow 3-axis self-spin + drag-to-rotate with inertia.
   Scheme-aware; honors prefers-reduced-motion; pauses offscreen. */
(function () {
  'use strict';

  var stage = document.querySelector('.emm-hero-stage');
  var canvas = document.querySelector('.emm-hero-cloud');
  if (!stage || !canvas) return;
  var MESH_MODE = !!window.EMM_MESH_V;
  if (!MESH_MODE && !window.EMM_POINTS) return;

  var gl = canvas.getContext('webgl', { alpha: true, antialias: true, powerPreference: 'low-power' });
  if (!gl) return;
  var derivExt = gl.getExtension('OES_standard_derivatives');
  if (MESH_MODE && !derivExt) MESH_MODE = false;
  if (!MESH_MODE && !window.EMM_POINTS) return;

  function decode(b64) {
    var raw = atob(b64);
    var bytes = new Uint8Array(raw.length);
    for (var i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
    return bytes.buffer;
  }

  /* ---------- model data ---------- */
  var meshVerts, meshIndex, NI = 0, pos, shade, N = 0;
  var MOBILE = Math.min(window.innerWidth, window.innerHeight) < 750;
  var MOB_PT = MOBILE ? 0.55 : 1.0;   // smaller particles on mobile
  var MOB_A = MOBILE ? 0.7 : 1.0;     // fainter on mobile so hero text stays readable
  if (MESH_MODE) {
    var vq = new Int16Array(decode(window.EMM_MESH_V));
    meshVerts = new Float32Array(vq.length);
    for (var vi = 0; vi < vq.length; vi++) meshVerts[vi] = (vq[vi] / 32767) * 0.5;
    meshIndex = new Uint16Array(decode(window.EMM_MESH_I));
    NI = window.EMM_MESH_NI;
  } else {
    var q = new Int16Array(decode(window.EMM_POINTS));
    var TOTAL = window.EMM_POINTS_N;
    N = MOBILE ? Math.floor(TOTAL / 2) : TOTAL;
    pos = new Float32Array(N * 3);
    for (var pi = 0; pi < N * 3; pi++) pos[pi] = (q[pi] / 32767) * 0.5;
    shade = new Float32Array(N);
    for (var si = 0; si < N; si++) shade[si] = Math.random();
  }

  /* ---------- stream particles ---------- */
  var M = MOBILE ? 640 : 1400;
  var sSeed = new Float32Array(M), sOff = new Float32Array(M), sSpd = new Float32Array(M);
  for (var mi = 0; mi < M; mi++) {
    sSeed[mi] = Math.random();
    sOff[mi] = (Math.random() - 0.5) * 2.0;
    sSpd[mi] = 0.6 + Math.random() * 0.9;
  }

  /* ---------- shaders ---------- */
  var ROTATE = [
    '  float cy = cos(uYaw), sy = sin(uYaw);',
    '  vec3 p = vec3(aPos.x*cy - aPos.z*sy, aPos.y, aPos.x*sy + aPos.z*cy);',
    '  float cx = cos(uPitch), sx = sin(uPitch);',
    '  p = vec3(p.x, p.y*cx - p.z*sx, p.y*sx + p.z*cx);',
    '  float cz = cos(uRoll), sz = sin(uRoll);',
    '  p = vec3(p.x*cz - p.y*sz, p.x*sz + p.y*cz, p.z);'
  ].join('\n');

  var VS_MESH = [
    'attribute vec3 aPos;',
    'uniform float uYaw;', 'uniform float uPitch;', 'uniform float uRoll;',
    'uniform float uAspect;', 'uniform float uScale;',
    'varying vec3 vP;', 'varying vec3 vP0;',
    'void main(){',
    ROTATE,
    '  vP = p;',
    '  vP0 = aPos;',
    '  vec2 xy = p.xy * uScale;',
    '  xy.x /= uAspect;',
    '  gl_Position = vec4(xy, p.z * 0.4, 1.0);',
    '}'
  ].join('\n');

  var FS_MESH = [
    '#extension GL_OES_standard_derivatives : enable',
    'precision mediump float;',
    'uniform vec3 uCol;', 'uniform float uAlpha;', 'uniform float uBands;',
    'uniform sampler2D uLogo;', 'uniform float uTopY;', 'uniform float uLogoS;',
    'varying vec3 vP;', 'varying vec3 vP0;',
    'float hash(vec2 c){ return fract(sin(dot(c, vec2(12.9898,78.233))) * 43758.5453); }',
    'void main(){',
    '  vec3 n = normalize(cross(dFdx(vP), dFdy(vP)));',
    '  vec3 l = normalize(vec3(0.45, 0.75, 0.5));',
    '  float lam = abs(dot(n, l));',
    '  float s = 0.34 + 0.66 * lam;',
    '  s = floor(s * uBands + 0.5) / uBands;',
    // Apple-logo indent on the top face: model-space normal up + near top plane
    '  vec3 n0 = normalize(cross(dFdx(vP0), dFdy(vP0)));',
    '  if (abs(n0.y) > 0.8 && vP0.y > uTopY - 0.02) {',
    '    vec2 uv = vec2(0.5 + vP0.x / uLogoS, 0.5 + vP0.z / uLogoS);',
    '    if (uv.x > 0.0 && uv.x < 1.0 && uv.y > 0.0 && uv.y < 1.0) {',
    '      float a = texture2D(uLogo, uv).a;',
    '      s *= 1.0 - a * 0.38;',                       // tone-on-tone indent
    '    }',
    '  }',
    '  s += (hash(gl_FragCoord.xy) - 0.5) * 0.07;',
    '  gl_FragColor = vec4(uCol * s, uAlpha);',
    '}'
  ].join('\n');

  var VS_CLOUD = [
    'attribute vec3 aPos;', 'attribute float aShade;',
    'uniform float uYaw;', 'uniform float uPitch;', 'uniform float uRoll;',
    'uniform float uAspect;', 'uniform float uDpr;', 'uniform float uScale;', 'uniform float uPtSize;',
    'varying float vShade;',
    'void main(){',
    ROTATE,
    '  vShade = aShade;',
    '  vec2 xy = p.xy * uScale;',
    '  xy.x /= uAspect;',
    '  gl_Position = vec4(xy, 0.0, 1.0);',
    '  gl_PointSize = uPtSize * uDpr;',
    '}'
  ].join('\n');

  var FS_CLOUD = [
    'precision mediump float;',
    'uniform vec3 uColA;', 'uniform vec3 uColB;', 'uniform float uAlpha;',
    'varying float vShade;',
    'void main(){',
    '  vec2 c = gl_PointCoord - 0.5;',
    '  if (dot(c, c) > 0.25) discard;',
    '  gl_FragColor = vec4(mix(uColB, uColA, vShade), uAlpha);',
    '}'
  ].join('\n');

  var VS_STREAM = [
    'attribute float aSeed;', 'attribute float aOff;', 'attribute float aSpd;',
    'uniform float uTime;', 'uniform float uAspect;', 'uniform float uDpr;', 'uniform float uSizeMul;', 'uniform float uVertical;',
    'varying float vFade;', 'varying float vCore;',
    'void main(){',
    '  float t = fract(uTime * 0.055 * aSpd + aSeed);',
    '  float flow = mix(-1.25, 1.25, t);',
    '  float core = smoothstep(0.85, 0.15, abs(flow));',
    '  float w = mix(0.55, 0.05, core);',
    '  float spread = aOff * w;',
    '  spread += sin(flow * 5.0 + aSeed * 40.0) * 0.012 * (1.0 - core);',
    '  vFade = smoothstep(0.0, 0.08, t) * smoothstep(1.0, 0.92, t);',
    '  vCore = core;',
    '  vec2 pos;',
    '  if (uVertical > 0.5) { pos = vec2(spread * 0.7, flow); }',
    '  else { pos = vec2(flow / max(uAspect * 0.55, 1.0), spread * 0.7); }',
    '  gl_Position = vec4(pos, 0.0, 1.0);',
    '  gl_PointSize = mix(1.2 + aSpd * 0.8, 1.7, core) * uSizeMul * uDpr;',
    '}'
  ].join('\n');

  var FS_STREAM = [
    'precision mediump float;',
    'uniform vec3 uColS;', 'uniform vec3 uColC;', 'uniform float uAlphaS;', 'uniform float uAlphaC;',
    'varying float vFade;', 'varying float vCore;',
    'void main(){',
    '  vec2 c = gl_PointCoord - 0.5;',
    '  if (dot(c, c) > 0.25) discard;',
    '  vec3 col = mix(uColS, uColC, vCore * 0.6);',
    '  float a = uAlphaS * vFade * (1.0 - smoothstep(0.25, 0.85, vCore) * 0.97);',
    '  gl_FragColor = vec4(col, a);',
    '}'
  ].join('\n');

  function program(vsSrc, fsSrc) {
    function sh(type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src); gl.compileShader(s);
      return s;
    }
    var p = gl.createProgram();
    gl.attachShader(p, sh(gl.VERTEX_SHADER, vsSrc));
    gl.attachShader(p, sh(gl.FRAGMENT_SHADER, fsSrc));
    gl.linkProgram(p);
    return gl.getProgramParameter(p, gl.LINK_STATUS) ? p : null;
  }

  var progModel = MESH_MODE ? program(VS_MESH, FS_MESH) : program(VS_CLOUD, FS_CLOUD);
  var progStream = program(VS_STREAM, FS_STREAM);
  if (!progModel || !progStream) return;

  function attr(prog, name, data, size) {
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, name);
    return { buf: buf, loc: loc, size: size };
  }
  function use(a) {
    gl.bindBuffer(gl.ARRAY_BUFFER, a.buf);
    gl.enableVertexAttribArray(a.loc);
    gl.vertexAttribPointer(a.loc, a.size, gl.FLOAT, false, 0, 0);
  }

  var aPos, aShade, indexBuf, logoTex, topY = 0;
  if (MESH_MODE) {
    aPos = attr(progModel, 'aPos', meshVerts, 3);
    indexBuf = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuf);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, meshIndex, gl.STATIC_DRAW);
    for (var ty = 1; ty < meshVerts.length; ty += 3) if (meshVerts[ty] > topY) topY = meshVerts[ty];

    // Apple-logo decal texture, drawn from precise bezier path data
    var lc = document.createElement('canvas');
    lc.width = lc.height = 512;
    var ctx = lc.getContext('2d');
    var path = new Path2D('M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701');
    ctx.translate(64, 64);            // margin so clamped edges stay transparent
    ctx.scale(16, 16);                // 24-unit path -> 384px in a 512px canvas
    ctx.fillStyle = '#000';
    ctx.fill(path);
    logoTex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, logoTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, lc);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  } else {
    aPos = attr(progModel, 'aPos', pos, 3);
    aShade = attr(progModel, 'aShade', shade, 1);
  }
  var aSeed = attr(progStream, 'aSeed', sSeed, 1);
  var aOff = attr(progStream, 'aOff', sOff, 1);
  var aSpd = attr(progStream, 'aSpd', sSpd, 1);

  function uni(p, n) { return gl.getUniformLocation(p, n); }
  var U = {
    m: { yaw: uni(progModel, 'uYaw'), pitch: uni(progModel, 'uPitch'), roll: uni(progModel, 'uRoll'), aspect: uni(progModel, 'uAspect'), dpr: uni(progModel, 'uDpr'), scale: uni(progModel, 'uScale'), ptSize: uni(progModel, 'uPtSize'), col: uni(progModel, 'uCol'), alpha: uni(progModel, 'uAlpha'), bands: uni(progModel, 'uBands'), logo: uni(progModel, 'uLogo'), topY: uni(progModel, 'uTopY'), logoS: uni(progModel, 'uLogoS'), colA: uni(progModel, 'uColA'), colB: uni(progModel, 'uColB') },
    s: { time: uni(progStream, 'uTime'), aspect: uni(progStream, 'uAspect'), dpr: uni(progStream, 'uDpr'), sizeMul: uni(progStream, 'uSizeMul'), vertical: uni(progStream, 'uVertical'), colS: uni(progStream, 'uColS'), colC: uni(progStream, 'uColC'), alphaS: uni(progStream, 'uAlphaS'), alphaC: uni(progStream, 'uAlphaC') }
  };

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  var dpr = Math.min(window.devicePixelRatio || 1, 2);
  var dark = false;
  function applyScheme() {
    dark = document.documentElement.getAttribute('data-emm-scheme') === 'dark';
  }
  applyScheme();
  new MutationObserver(applyScheme).observe(document.documentElement, { attributes: true, attributeFilter: ['data-emm-scheme'] });

  /* ---- drag-to-rotate with inertia ---- */
  var userYaw = 0, userPitch = 0, velYaw = 0, velPitch = 0;
  var dragging = false, lastX = 0, lastY = 0;
  function dragStart(e) {
    if (e.target.closest && e.target.closest('a, button, input, select, textarea')) return;
    dragging = true; lastX = e.clientX; lastY = e.clientY;
    stage.classList.add('emm-grabbing');
  }
  function dragMove(e) {
    if (!dragging) return;
    var dx = e.clientX - lastX, dy = e.clientY - lastY;
    lastX = e.clientX; lastY = e.clientY;
    velYaw = dx * 0.006; velPitch = dy * 0.006;
    userYaw += velYaw; userPitch += velPitch;
    userPitch = Math.max(-1.2, Math.min(1.2, userPitch));
  }
  function dragEnd() { dragging = false; stage.classList.remove('emm-grabbing'); }
  stage.addEventListener('pointerdown', dragStart);
  window.addEventListener('pointermove', dragMove);
  window.addEventListener('pointerup', dragEnd);
  window.addEventListener('pointercancel', dragEnd);

  var aspect = 1, scaleVal = 2.0;
  function resize() {
    var w = stage.clientWidth, h = stage.clientHeight;
    canvas.width = w * dpr; canvas.height = h * dpr;
    gl.viewport(0, 0, canvas.width, canvas.height);
    aspect = w / h;
    scaleVal = Math.max(1.1, Math.min(2.4, 1760 / h));
    // On mobile (portrait) the model overflows the screen width. Cap the scale
    // so it stays narrower than the viewport. (Tune this number if needed.)
    if (MOBILE) scaleVal = Math.min(scaleVal, 1.05);
  }
  resize();
  window.addEventListener('resize', resize);

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var visible = true;
  new IntersectionObserver(function (e) { visible = e[0].isIntersecting; }).observe(stage);

  var start = performance.now();
  function draw(now) {
    var t = (now - start) / 1000;
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    if (!dragging) {
      userYaw += velYaw; userPitch += velPitch;
      velYaw *= 0.95; velPitch *= 0.95;
      userPitch = Math.max(-1.2, Math.min(1.2, userPitch));
    }
    var yaw = t * 0.055 + userYaw;                            // continuous spin
    var pitch = 0.32 + Math.sin(t * 0.05) * 0.28 + userPitch;  // pronounced nod
    var roll = t * 0.035;                                      // continuous slow tumble

    gl.useProgram(progModel);
    use(aPos);
    gl.uniform1f(U.m.yaw, yaw);
    gl.uniform1f(U.m.pitch, pitch);
    gl.uniform1f(U.m.roll, roll);
    gl.uniform1f(U.m.aspect, aspect);
    gl.uniform1f(U.m.scale, scaleVal);

    if (MESH_MODE) {
      gl.uniform1f(U.m.bands, 5.0);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, logoTex);
      gl.uniform1i(U.m.logo, 0);
      gl.uniform1f(U.m.topY, topY);
      gl.uniform1f(U.m.logoS, 0.17);   // logo footprint ~17% of chassis width
      if (dark) {
        gl.uniform3f(U.m.col, 0.62, 0.76, 0.68);   // sage facets on near-black
        gl.uniform1f(U.m.alpha, 0.92);
      } else {
        gl.uniform3f(U.m.col, 0.42, 0.56, 0.49);   // grey-green facets on white
        gl.uniform1f(U.m.alpha, 0.92);
      }
      gl.enable(gl.DEPTH_TEST);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuf);
      gl.drawElements(gl.TRIANGLES, NI, gl.UNSIGNED_SHORT, 0);
      gl.disable(gl.DEPTH_TEST);
    } else {
      use(aShade);
      gl.uniform1f(U.m.dpr, dpr);
      gl.uniform1f(U.m.ptSize, (dark ? 2.0 : 3.4) * MOB_PT);
      if (dark) {
        gl.uniform3f(U.m.colA, 0.30, 0.40, 0.34);   // darker sage — recedes behind text
        gl.uniform3f(U.m.colB, 0.33, 0.36, 0.34);
        gl.uniform1f(U.m.alpha, 0.30 * MOB_A);
      } else {
        gl.uniform3f(U.m.colA, 0.08, 0.18, 0.13);   // near-ink green — strong on white
        gl.uniform3f(U.m.colB, 0.27, 0.30, 0.28);
        gl.uniform1f(U.m.alpha, 0.60 * MOB_A);
      }
      gl.drawArrays(gl.POINTS, 0, N);
    }

    gl.useProgram(progStream);
    use(aSeed); use(aOff); use(aSpd);
    gl.uniform1f(U.s.time, t);
    gl.uniform1f(U.s.aspect, aspect);
    gl.uniform1f(U.s.dpr, dpr);
    gl.uniform1f(U.s.sizeMul, (dark ? 1.0 : 1.5) * MOB_PT);
    gl.uniform1f(U.s.vertical, MOBILE ? 1.0 : 0.0);
    if (dark) {
      gl.uniform3f(U.s.colS, 0.42, 0.85, 0.62);
      gl.uniform3f(U.s.colC, 0.62, 0.76, 0.68);
      gl.uniform1f(U.s.alphaS, 0.66 * MOB_A);
      gl.uniform1f(U.s.alphaC, 0.5 * MOB_A);
    } else {
      gl.uniform3f(U.s.colS, 0.03, 0.27, 0.17);
      gl.uniform3f(U.s.colC, 0.20, 0.32, 0.26);
      gl.uniform1f(U.s.alphaS, 0.72 * MOB_A);
      gl.uniform1f(U.s.alphaC, 0.42 * MOB_A);
    }
    gl.drawArrays(gl.POINTS, 0, M);
  }

  function frame(now) {
    if (visible) draw(now);
    requestAnimationFrame(frame);
  }
  if (reduced) {
    draw(start + 9000);
  } else {
    requestAnimationFrame(frame);
  }
})();
